<script setup>
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import SquareIconButton from "@/components/global-themed/buttons/SquareIconButton.vue";
import {useStore} from "vuex";
import {computed} from "vue";
import {errorToast, successToast} from "@/composables/useToasts";

defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const queryClient = useQueryClient();

const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);
const monthsFilter = computed(() => store.getters['actions/monthsFilter']);

const {mutate: markIncomplete} = useMutation({
  mutationFn: actionId => axios.post(`/ajax/v2/actions/${actionId}/mark-incomplete`),
  onMutate: async (actionId) => {
    await queryClient.cancelQueries({ queryKey: ['actions'] });

    const previousActions = queryClient.getQueryData(['actions', activeTab, monthsFilter]);

    queryClient.setQueryData(['actions', activeTab, monthsFilter], (old) => {
      if (!old) {
        return [];
      }

     return {
        ...old,
        actions: old.actions.filter(action => action.id !== actionId)
     }
    });

    successToast('Action marked as incomplete');

    return { previousActions }
  },
  onError: (error, _, context) => {
    const {message} = error.response.data
    errorToast(message ?? 'Something went wrong marking action as incomplete');

    queryClient.setQueryData(['actions', activeTab, monthsFilter], context.previousActions);
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['actions'] });
    queryClient.invalidateQueries({ queryKey: ['action-progress'] });
  },
});
</script>

<template>
  <SquareIconButton
    v-tooltip="{
      content: 'Mark this action as incomplete',
      theme: 'dark-blue-tooltip',
    }"
    variant="primary-inverted"
    size="small"
    @click="markIncomplete(action.id)"
  >
    <svg-vue
      icon="icons/revert"
      class="tw-h-4 tw-w-4"
    />
  </SquareIconButton>
</template>
